import { createAsyncThunk } from '@reduxjs/toolkit'
import { isAxiosError } from 'axios'

import { apiGET, apiPOST } from '../lib/api'
import db from '../lib/db'
import { getErrorString } from '../lib/errors'
import { error } from '../lib/logger'
import type { FeedbackSliceState } from '../reducers/feedbackSlice'
import type { Feedback, NewFeedback } from '../types/Feedback'

type FeedbackThunkConfig = { rejectValue: string; state: { feedbacks: FeedbackSliceState } }

const isConflict = (err: unknown): boolean => isAxiosError(err) && err.response?.status === 409

const replaceCachedFeedbacks = (feedbacks: Feedback[]) =>
  db.transaction('rw', db.feedbacks, async () => {
    await db.feedbacks.clear()
    await db.feedbacks.bulkPut(feedbacks)
  })

const withSentFeedbacks = (feedbacks: Feedback[], state: FeedbackSliceState): Feedback[] => {
  const fetched = new Set(feedbacks.map((feedback) => feedback.targetId))
  const sent = Object.keys(state.send)
    .filter((targetId) => state.send[targetId].status === 'succeeded' && !fetched.has(targetId))
    .map((targetId) => state.byTargetId[targetId])
    .filter(Boolean)
  return [...feedbacks, ...sent]
}

export const clearCachedFeedbacks = (): Promise<void> => db.feedbacks.clear()

export const loadCachedFeedbacks = createAsyncThunk(
  'feedbacks/loadCached',
  (): Promise<Feedback[]> => db.feedbacks.toArray()
)

/** loads cached feedbacks from indexedDB, then from backend */
export const loadFeedbacks = createAsyncThunk<Feedback[], void, FeedbackThunkConfig>(
  'feedbacks/load',
  async (_: void, thunkApi) => {
    await thunkApi.dispatch(loadCachedFeedbacks())

    let feedbacks: Feedback[]
    try {
      const res = await apiGET<{ feedbacks: Feedback[] }>('/me/feedback')
      feedbacks = withSentFeedbacks(res.feedbacks, thunkApi.getState().feedbacks)
    } catch (err) {
      return thunkApi.rejectWithValue(getErrorString(err, 'feedback'))
    }

    await replaceCachedFeedbacks(feedbacks).catch((e) => error('Caching feedbacks failed', e))
    return feedbacks
  }
)

export const sendFeedback = createAsyncThunk<Feedback, NewFeedback, FeedbackThunkConfig>(
  'feedbacks/send',
  async (newFeedback, thunkApi) => {
    let feedback: Feedback
    try {
      feedback = await apiPOST<Feedback>('/me/feedback', newFeedback)
    } catch (err) {
      // Already sent: show the stored feedback instead
      if (isConflict(err)) await thunkApi.dispatch(loadFeedbacks())
      return thunkApi.rejectWithValue(getErrorString(err, 'feedback'))
    }

    await db.feedbacks.put(feedback).catch((e) => error('Caching sent feedback failed', e))
    return feedback
  }
)
